import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import debounce from 'lodash/debounce';
import { useSnackbar } from 'notistack';

import { Alert, Box, Button, Snackbar, Stack } from '@mui/material';

import FlatImageEditor from '~/components/Common/Images/FlatImageEditor';
import {
  mergeFlatItemsWithLegacy,
  sortImagesByOrder,
  toFlatImageItem,
} from '~/components/Common/Images/legacyImageAdapter';
import {
  FlatImageEditorHandle,
  FlatImageEditorPendingUploadState,
  FlatImageItem,
  FlatImageUploadPayload,
} from '~/components/Common/Images/types';

import { getUploadedImageId, uploadImage } from '~/services/ImageService';

import getErrorMessage from '~/utils/getErrorMessage';

import { reportError } from '../../../utils/reportError';

import { buttonMessages as baseButtonMessages, buttonStates } from './states/submitButton';

const buttonMessages = { ...baseButtonMessages };
buttonMessages[buttonStates.default] = 'Save changes';

interface Image {
  id: string | number;
  publicImageId?: string;
  id_cloudinary_external?: string;
  imageId?: string;
  order?: number;
  title?: string;
  hidden?: boolean;
  filename?: string;
  is_hero?: boolean;
  source?: string;
  description?: string;
}

interface Props {
  images: Array<Image>;
  onAddImage: (cloudinaryId: string, filename: string, desiredOrder?: number) => Promise<unknown>;
  onDeleteImage?: (imageId: string | number) => Promise<unknown> | unknown;
  onUpdateImages: (images: Array<Image>) => Promise<unknown> | unknown;
  enablePendingCropUpload?: boolean;
  hasHiddenToggle?: boolean;
  hideDeleteButton?: boolean;
  onPendingUploadStateChange?: (state: FlatImageEditorPendingUploadState) => void;
  showSaveButton?: boolean;
  // When false, edits and deletions stay local until the Save button is pressed instead of persisting immediately.
  autoSaveOnChange?: boolean;
  headerActions?: React.ReactNode;
}

const ImagesForm = forwardRef(function ImagesForm(props: Props, ref) {
  const {
    enablePendingCropUpload = false,
    hasHiddenToggle,
    headerActions,
    hideDeleteButton = false,
    images: incomingImages,
    onAddImage,
    onDeleteImage,
    onPendingUploadStateChange,
    onUpdateImages,
    showSaveButton,
    autoSaveOnChange = true,
  } = props;
  const { enqueueSnackbar } = useSnackbar();
  const [saveButtonState, setSaveButtonState] = useState(buttonStates.default);
  const [images, setImages] = useState<Array<Image>>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const imagesRef = useRef<Array<Image>>([]);
  const lastIncomingContentRef = useRef<string | undefined>(undefined);
  const stagedDeleteIdsRef = useRef<Array<string | number>>([]);
  const imageEditorRef = useRef<FlatImageEditorHandle | null>(null);

  useEffect(() => {
    // Parents can re-send identical images with a new array identity; don't wipe unsaved edits for that.
    const incomingContent = JSON.stringify(incomingImages);

    if (!autoSaveOnChange && incomingContent === lastIncomingContentRef.current) {
      return;
    }

    lastIncomingContentRef.current = incomingContent;
    stagedDeleteIdsRef.current = [];
    const sortedImages = sortImagesByOrder(incomingImages);
    imagesRef.current = sortedImages;
    setImages(sortedImages);
    setHasUnsavedChanges(false);
  }, [incomingImages, autoSaveOnChange]);

  const shouldShowSaveButton = showSaveButton !== undefined ? showSaveButton : true;
  const isButtonStateSaving = saveButtonState === buttonStates.saving;
  const hasSaveFailed = saveButtonState === buttonStates.failed;

  const debouncedPersistImages = useMemo(
    () =>
      debounce((nextImages: Array<Image>) => {
        Promise.resolve(onUpdateImages(nextImages)).catch(() => {
          setSaveButtonState(buttonStates.failed);
        });
      }, 1000),
    [onUpdateImages],
  );

  useEffect(() => {
    return () => {
      debouncedPersistImages.cancel();
    };
  }, [debouncedPersistImages]);

  useImperativeHandle(ref, () => ({
    addImageToList(newImages: Array<Image>) {
      const combinedImages = sortImagesByOrder(images.concat(newImages));
      imagesRef.current = combinedImages;
      setImages(combinedImages);
      onUpdateImages(combinedImages);
    },
    uploadAllPending() {
      return (
        imageEditorRef.current?.uploadAllPending?.() ??
        Promise.resolve({
          uploadedCount: 0,
          failedCount: 0,
          failedItems: [],
        })
      );
    },
  }));

  const flatItems = useMemo(() => images.map((image) => toFlatImageItem(image)), [images]);

  const handleItemsChange = (nextItems: Array<FlatImageItem>) => {
    if (saveButtonState !== buttonStates.default) {
      setSaveButtonState(buttonStates.default);
    }

    const currentImages = imagesRef.current;
    const nextImages = mergeFlatItemsWithLegacy(currentImages, nextItems);
    imagesRef.current = nextImages;
    setImages(nextImages);

    if (autoSaveOnChange) {
      debouncedPersistImages(nextImages);
    } else {
      setHasUnsavedChanges(true);
    }
  };

  const handleUploadFile = async ({ description, desiredOrder, file, filename }: FlatImageUploadPayload) => {
    try {
      const uploadResponse = await uploadImage(file);
      const uploadedImageId = getUploadedImageId(uploadResponse);

      if (!uploadedImageId) {
        throw new Error('Image service did not return an image id');
      }

      const response = (await onAddImage(uploadedImageId, filename, desiredOrder)) as Partial<Image>;
      const currentImageCount = imagesRef.current.length;
      return toFlatImageItem({
        ...response,
        id: response.id ?? uploadedImageId,
        filename: response.filename ?? filename,
        order: response.order ?? desiredOrder ?? currentImageCount + 1,
        publicImageId: response.publicImageId ?? response.id_cloudinary_external ?? response.imageId ?? uploadedImageId,
        title: description || response.title,
        description: description || response.description,
      });
    } catch (error) {
      enqueueSnackbar(`Error uploading image: ${getErrorMessage(error, 'Unknown error')}`, { variant: 'error' });
      reportError(error);
      throw error;
    }
  };

  const handleDelete = async (imageId: string | number) => {
    if (!onDeleteImage) {
      return;
    }

    if (!autoSaveOnChange) {
      stagedDeleteIdsRef.current = [...stagedDeleteIdsRef.current.filter((id) => id !== imageId), imageId];
      return;
    }

    await Promise.resolve(onDeleteImage(imageId));
  };

  // One at a time, dropping each id once it is deleted so a retry after a failure only repeats the rest.
  const deleteStagedImages = async () => {
    while (stagedDeleteIdsRef.current.length > 0) {
      const [nextId] = stagedDeleteIdsRef.current;
      await Promise.resolve(onDeleteImage?.(nextId));
      stagedDeleteIdsRef.current = stagedDeleteIdsRef.current.filter((id) => id !== nextId);
    }
  };

  const handleUndoChanges = () => {
    const savedImages = sortImagesByOrder(incomingImages);
    imagesRef.current = savedImages;
    stagedDeleteIdsRef.current = [];
    setImages(savedImages);
    setHasUnsavedChanges(false);
    setSaveButtonState(buttonStates.default);
  };

  const handleSaveImages = async () => {
    setSaveButtonState(buttonStates.saving);

    try {
      const uploadResult = await imageEditorRef.current?.uploadAllPending?.();

      if (uploadResult?.failedCount) {
        setSaveButtonState(buttonStates.failed);
        return;
      }

      debouncedPersistImages.cancel();
      await deleteStagedImages();
      const savedImages = imagesRef.current;
      await Promise.resolve(onUpdateImages(savedImages));
      setSaveButtonState(buttonStates.saved);

      if (imagesRef.current === savedImages) {
        setHasUnsavedChanges(false);
      }
    } catch (error) {
      reportError(error);
      setSaveButtonState(buttonStates.failed);
    }
  };

  return (
    <Box mt={1} className="T-images-form">
      {shouldShowSaveButton || headerActions ? (
        <Box display="flex" alignItems="center" gap={2}>
          {shouldShowSaveButton ? (
            <Button disabled={isButtonStateSaving} variant="contained" onClick={handleSaveImages}>
              {buttonMessages[saveButtonState]}
            </Button>
          ) : null}
          {headerActions}
        </Box>
      ) : null}

      {/* Persistent (no autoHideDuration/onClose); centred with flex since MUI's translateX(-50%) can land on a half pixel. */}
      <Snackbar
        open={hasUnsavedChanges}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ left: 0, right: 0, transform: 'none', pointerEvents: 'none' }}
      >
        <Alert
          severity={hasSaveFailed ? 'error' : 'info'}
          sx={{ pointerEvents: 'auto' }}
          action={
            <Stack direction="row" spacing={1}>
              <Button variant="text" size="small" disabled={isButtonStateSaving} onClick={handleUndoChanges}>
                Undo
              </Button>
              <Button
                variant="contained"
                color="primary"
                size="small"
                disabled={isButtonStateSaving}
                onClick={handleSaveImages}
              >
                {isButtonStateSaving ? 'Saving...' : 'Save changes'}
              </Button>
            </Stack>
          }
        >
          {hasSaveFailed
            ? 'Failed to save image changes. Your edits are still here, try saving again.'
            : "Unsaved image changes. They won't be applied until you save."}
        </Alert>
      </Snackbar>

      <FlatImageEditor
        ref={imageEditorRef}
        deleteActionLabel="Delete image"
        deleteUndoMs={autoSaveOnChange ? undefined : 0}
        enablePendingCropUpload={enablePendingCropUpload}
        items={flatItems}
        onPendingUploadStateChange={onPendingUploadStateChange}
        showDeleteAction={!hideDeleteButton}
        showDescriptionField
        showHiddenToggle={hasHiddenToggle}
        uploadLabel="Upload images"
        onDelete={handleDelete}
        onItemsChange={handleItemsChange}
        onUploadFile={handleUploadFile}
      />
    </Box>
  );
});

ImagesForm.displayName = 'ImagesForm';

export default ImagesForm;
